import { z } from 'zod';
/**
 * Onboarding reveal · post-wizard "magic moment" page.
 *
 * Source-of-truth design lives in
 * `tmp/onboarding-mock/bundle/leanopx/project/assets/agentic.jsx`
 * (lines 107–295). Three panels (People · Rituals · Metrics) each
 * carrying a 0..100 confidence percent, plus a vocabulary translation
 * table and a "read complete" header showing duration + read counters.
 *
 * Distinct from the existing `onboarding/reveal.ts` surface, which
 * powers `GET /api/orgs/:o/onboarding/reveal` with 0..1 confidence
 * fractions, fewer fields, and the older "people / rituals / metrics
 * arrays at the top level" shape. This file models the NEW redesigned
 * surface (`OnboardingReveal` component in `agentic.jsx`) where
 * confidence is a percent, color tokens travel with each row, the
 * read duration is exposed in minutes, and the user can approve or
 * correct the three panels independently. We add fresh schemas
 * rather than mutating the existing wire shape so both surfaces can
 * coexist while the redesign rolls out.
 *
 * Naming uses an explicit `RevealPanel*` / `Reveal*Card` prefix so
 * downstream consumers can disambiguate from the older `RevealPerson`
 * / `RevealRitual` / `RevealMetric` symbols in `onboarding/reveal.ts`.
 *
 * Notable LearnedFact rename: the row primitive surfaced inside the
 * reveal panels is named `OnboardingRevealLearnedFact` to avoid a
 * collision with the unrelated `LearnedFact` schema in
 * `learned-facts.ts` (which models per-user enrichment-derived rows on
 * `/profile`).
 */
export const OnboardingRevealPanelKind = z.enum(['people', 'rituals', 'metrics']);
/**
 * Source provenance for a learned fact — `{ kind, n }` mirrors the
 * `<Cite src=... n=... />` props the mock uses (e.g. `linkedin · 1`,
 * `crunchbase · 812`). `kind` is intentionally free-text because the
 * mock cites a long tail of source vendors; the closed
 * `FactSource` enum in `fact-provenance.ts` is structurally narrower
 * and tied to a different surface family.
 */
export const OnboardingRevealSource = z.object({
    kind: z.string().min(1).max(40),
    n: z.number().int().min(0),
});
/**
 * One row in any of the three reveal panels — the agent's individual
 * "I learned X" claim, with a 0..100 confidence percent and a list of
 * supporting sources. The operator can approve or correct each row
 * independently; the approve/correct timestamps + actor uid are stamped
 * server-side and read back here so the UI can show a "✓ approved by
 * you" affordance.
 */
export const OnboardingRevealLearnedFact = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    panelKind: OnboardingRevealPanelKind,
    /** Human-readable claim — "Jenna is one of the 12 people you care about." */
    claim: z.string().min(1).max(400),
    /** 0..100 percent (matches the mock's "87% conf" pill format). */
    confidence: z.number().min(0).max(100),
    sources: z.array(OnboardingRevealSource).max(20),
    /** ISO-8601 timestamp; null when the operator hasn't approved yet. */
    approvedAt: z.string().datetime().nullable(),
    /** Firebase uid of the approver; null when not approved. */
    approvedBy: z.string().min(1).nullable(),
    /** ISO-8601 timestamp of the operator's correction; null when uncorrected. */
    correctedAt: z.string().datetime().nullable(),
    /** Free-text replacement value; null when uncorrected. */
    correctedTo: z.string().max(2000).nullable(),
});
/** Person-panel row — initials avatar + display name + color token. */
export const OnboardingRevealPeopleMember = z.object({
    /** Two- or three-letter initials shown in the avatar circle. */
    initials: z.string().min(1).max(3),
    name: z.string().min(1).max(120),
    /** CSS var token, e.g. `var(--color-accent)`. Travels with the row so
     *  the renderer doesn't need a parallel color-derivation pass. */
    colorToken: z.string().min(1).max(80),
});
/** Ritual-panel row — title + cadence prose + supporting note. */
export const OnboardingRevealRitualItem = z.object({
    title: z.string().min(1).max(120),
    /** Human-readable cadence label — "Friday 9:00am", "Last Wed of month". */
    when: z.string().min(1).max(80),
    /** Supporting note — "Weekly · 90 min · 6 people". */
    note: z.string().min(1).max(160),
});
/** Metric-panel row — label + formatted value + delta + tone token. */
export const OnboardingRevealMetricItem = z.object({
    label: z.string().min(1).max(120),
    /** Formatted display value — "$22,224", "3.30×". */
    value: z.string().min(1).max(40),
    /** Trend delta — "+3% wk", "−0.4× wk", "steady". */
    delta: z.string().min(1).max(40),
    /** CSS var token for the tone dot — `var(--color-sage)` / `var(--color-rose)` / `var(--color-steel)`. */
    toneToken: z.string().min(1).max(80),
});
/** Top-level reveal summary returned to the page. */
export const OnboardingRevealSummary = z.object({
    orgId: z.string().min(1),
    /** ISO-8601 timestamp the synthesizer finished. */
    generatedAt: z.string().datetime(),
    /** Total wall-clock duration of the read in minutes — "6 hours, 23 minutes" → 383. */
    readDurationMinutes: z.number().int().min(0),
    messagesRead: z.number().int().min(0),
    docsRead: z.number().int().min(0),
    recordsRead: z.number().int().min(0),
    people: z.object({
        confidence: z.number().min(0).max(100),
        members: z.array(OnboardingRevealPeopleMember).max(24),
    }),
    rituals: z.object({
        confidence: z.number().min(0).max(100),
        items: z.array(OnboardingRevealRitualItem).max(12),
    }),
    metrics: z.object({
        confidence: z.number().min(0).max(100),
        items: z.array(OnboardingRevealMetricItem).max(8),
    }),
    /**
     * Vocabulary translation pairs — `{generic, yours}` objects.
     *
     * Historically a tuple-of-tuples (`Array<[string, string]>`) but
     * Firestore Datastore-mode REJECTS nested arrays with
     * `INVALID_ARGUMENT: Nested arrays are not allowed`, which caused
     * the persist path in `apps/functions/src/firestore/onboarding-
     * reveal-store.ts` to silently fail on every write. Flattened to
     * objects so the persist round-trips cleanly.
     */
    vocabulary: z
        .array(z.object({
        generic: z.string().min(1).max(40),
        yours: z.string().min(1).max(40),
    }))
        .max(20),
});
/**
 * Body of `POST /api/orgs/:o/onboarding/reveal/approve`. Operator
 * confirms each panel independently; the handler stamps the
 * matching `approvedAt`/`approvedBy` on every `OnboardingRevealLearnedFact`
 * row in the approved panel(s) and records an `agentAction.synthesized`
 * audit row.
 */
export const OnboardingRevealApproveRequest = z.object({
    panels: z.object({
        people: z.boolean(),
        rituals: z.boolean(),
        metrics: z.boolean(),
    }),
});
